/**
 * PayloadDiffTable — render a change_request payload as a Field / Old / New
 * 3-column table on the Compliance review page.
 *
 * Beta #1 (2026-05-26): replaces the raw JSONB dump on
 * ChangeRequestDetailPage with a reviewer-friendly side-by-side diff.
 *
 * Behaviour per CR kind:
 *  - CREATE  → Field / — / New           (no live entity to compare)
 *  - UPDATE  → Field / Old / New         (fetches live entity by id)
 *  - DELETE  → friendly summary; if cascade payload (_cascade_ref_ids)
 *              present, render the cascade group counts
 *  - RESTORE → friendly summary
 *
 * Side-channel keys in the payload that aren't entity columns are
 * hidden from the table (prefix `_`, e.g. `_cascade_ref_ids`,
 * `_post_apply_account_ids`).
 *
 * The "Old" column is best-effort. If the live entity fetch fails or
 * the entity has been hard-deleted, the column falls back to em-dash.
 * The "New" column always renders from the CR payload.
 */
import { Alert, Code, Skeleton, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { EntityFriendlyLabel } from './EntityFriendlyLabel';
import { accountsApi } from '../api/accounts';
import { agreementDocumentsApi } from '../api/agreementDocuments';
import { agreementPartiesApi } from '../api/agreementParties';
import { agreementsApi } from '../api/agreements';
import { accountInsuranceApi } from '../api/accountInsurance';
import { accountPartiesApi } from '../api/accountParties';
import { insurancePolicyPartiesApi } from '../api/insurancePolicyParties';
import { personsApi } from '../api/persons';
import type { ChangeRequest } from '../api/types';
import { pepLabel, usPersonLabel } from '../utils/pepLabels';
import { CLIENT_RECORD_BLOCKS, describeClientBlock } from './ClientRecordFields';
import { commsValueLabel, isCommsField } from '../utils/commsPreferences';

// entity_type → api module with .get(id) — must match backend
// services/maker_checker.py ENTITY_REGISTRY.
const ENTITY_GETTERS: Record<string, (id: string) => Promise<unknown>> = {
  agreements:                (id) => agreementsApi.get(id),
  agreement_documents:       (id) => agreementDocumentsApi.get(id),
  persons:                   (id) => personsApi.get(id),
  accounts:                  (id) => accountsApi.get(id),
  insurance_policies:        (id) => accountInsuranceApi.get(id),
  agreement_parties:         (id) => agreementPartiesApi.get(id),
  account_parties:           (id) => accountPartiesApi.get(id),
  insurance_policy_parties:  (id) => insurancePolicyPartiesApi.get(id),
  // Post-0028 (2026-06-05): the join table was collapsed into
  // Account.opened_by_policy_id. No per-row GET exists anymore;
  // historical audit_log diffs that reference this entity_type
  // render the raw UUID prefix via the default fallback.
  insurance_policy_accounts: () => Promise.resolve(null),
};

interface PayloadDiffTableProps {
  cr: ChangeRequest;
}

// Beta #12 follow-up (2026-05-26): when a diff cell holds a FK UUID,
// resolve it to the same friendly label used on the CR detail "Record"
// row. The map below names every field we want to auto-resolve. Bare
// person_id / account_id / agreement_id / policy_id cover the join
// tables (agreement_parties, account_parties, insurance_policy_*);
// kyc_contact_person_id covers Beta #3's snapshot FK.
const FK_FIELD_MAP: Record<string, string> = {
  person_id:               'persons',
  kyc_contact_person_id:   'persons',
  client_origin_referrer_id: 'persons',   // P15 (migration 0099): the referring Person
  account_id:              'accounts',
  agreement_id:            'agreements',
  policy_id:               'insurance_policies',
  // Pedro's smoke test 10 (2026-06-10): attach-to-policy CRs carry the
  // target policy under this post-0028 FK name — resolve it like the rest.
  opened_by_policy_id:     'insurance_policies',
  related_party_id:        'agreement_parties',
  bank_id:                 'banks',               // the account's bank (Banks list, 0112)
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Render a value as a string. Null/undefined → em-dash so blank cells
// stand out clearly from empty strings. When fieldName matches an
// FK_FIELD_MAP entry and the value is a UUID, render an
// EntityFriendlyLabel instead of the raw UUID.
function formatValue(v: unknown, fieldName?: string): React.ReactNode {
  if (v === null || v === undefined) {
    return <Text c="dimmed" component="span">—</Text>;
  }
  if (v === '') {
    return <Text c="dimmed" size="xs" component="span">(empty string)</Text>;
  }
  // P4 (2026-10-09): PEP / US person read in English; a stored Portuguese
  // PEP value stays visible in brackets.
  if (fieldName === 'pep_status' && typeof v === 'string') {
    const en = pepLabel(v);
    return en === v ? v : `${en} (${v})`;
  }
  if (fieldName === 'us_person' && typeof v === 'boolean') {
    return usPersonLabel(v);
  }
  // P13: email preferences read as Receives / Does not receive.
  if (isCommsField(fieldName) && typeof v === 'boolean') {
    return commsValueLabel(v);
  }
  if (typeof v === 'boolean') {
    return v ? 'true' : 'false';
  }
  // P16b: the client-record blocks read as lines, not JSON (§3j).
  if (fieldName && CLIENT_RECORD_BLOCKS.has(fieldName) && typeof v === 'object') {
    const lines = describeClientBlock(fieldName, v);
    return lines.length
      ? <div>{lines.map((l, i) => <Text key={i} size="xs" component="div">{l}</Text>)}</div>
      : <Text c="dimmed" component="span">—</Text>;
  }
  if (typeof v === 'object') {
    return <Code style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(v)}</Code>;
  }
  const out = String(v);
  if (fieldName && FK_FIELD_MAP[fieldName] && UUID_RE.test(out)) {
    return (
      <EntityFriendlyLabel
        entityType={FK_FIELD_MAP[fieldName]}
        entityId={out}
      />
    );
  }
  return out;
}

// Side-channel keys we never show in the diff. They're not entity
// columns and the maker-checker apply path consumes them separately.
const HIDDEN_KEYS = new Set<string>([
  '_cascade_ref_ids',
  '_post_apply_account_ids',
]);

function visiblePayloadEntries(payload: Record<string, unknown> | null | undefined):
  Array<[string, unknown]> {
  if (!payload || typeof payload !== 'object') return [];
  return Object.entries(payload)
    .filter(([k]) => !HIDDEN_KEYS.has(k) && !k.startsWith('_'))
    .sort(([a], [b]) => a.localeCompare(b));
}

export function PayloadDiffTable({ cr }: PayloadDiffTableProps) {
  const needsLiveFetch = cr.kind === 'UPDATE' && !!cr.entity_id;
  const getter = ENTITY_GETTERS[cr.entity_type];

  const liveQuery = useQuery<unknown, Error>({
    queryKey: ['cr-diff-live', cr.entity_type, cr.entity_id],
    queryFn: () => getter!(cr.entity_id!),
    enabled: needsLiveFetch && !!getter,
    // The live entity is needed for one render; don't refetch on focus.
    staleTime: 60_000,
    retry: false,
  });

  // ---- RESTORE: no per-field diff, just a friendly summary ----
  if (cr.kind === 'RESTORE') {
    return (
      <Alert color="blue" variant="light">
        Restoring this {cr.entity_type} record. The previous soft-delete
        will be reversed; no field values change.
      </Alert>
    );
  }

  // ---- DELETE: friendly summary + optional cascade group counts ----
  if (cr.kind === 'DELETE') {
    const cascadeRefs = (cr.payload?._cascade_ref_ids ?? null) as
      Record<string, string[]> | null;
    return (
      <>
        <Alert color="orange" variant="light" mb="sm">
          Soft-deleting this {cr.entity_type} record. The row will be
          marked deleted but retained for the audit trail.
        </Alert>
        {cascadeRefs && Object.keys(cascadeRefs).length > 0 && (
          <Table withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Cascade attachment table</Table.Th>
                <Table.Th>Rows to soft-delete</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {Object.entries(cascadeRefs).map(([table, ids]) => (
                <Table.Tr key={table}>
                  <Table.Td><Code>{table}</Code></Table.Td>
                  <Table.Td>{ids.length}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </>
    );
  }

  // ---- CREATE / UPDATE: per-field 3-column table ----
  const allEntries = visiblePayloadEntries(cr.payload as Record<string, unknown>);
  const live = (liveQuery.data ?? {}) as Record<string, unknown>;

  // Beta v2 polish A (2026-05-26): for UPDATE, the AgreementUpdate Pydantic
  // model accepts ALL mutable fields and the frontend sends every populated
  // form value (not a true diff), so the CR payload includes many fields
  // that didn't actually change. Filter to only the rows where old !== new
  // so the reviewer sees a clean diff. CREATE shows all; DELETE handled above.
  const entries =
    cr.kind === 'UPDATE' && !liveQuery.isLoading && !liveQuery.isError
      ? allEntries.filter(([k, newVal]) => {
          const oldVal = live[k];
          const oldNorm = oldVal === null || oldVal === undefined || oldVal === '' ? null : oldVal;
          const newNorm = newVal === null || newVal === undefined || newVal === '' ? null : newVal;
          return JSON.stringify(oldNorm) !== JSON.stringify(newNorm);
        })
      : allEntries;

  if (entries.length === 0) {
    return (
      <Alert color="gray" variant="light">
        {cr.kind === 'UPDATE'
          ? 'No field actually changed between the request payload and the live row.'
          : 'No fields proposed for change.'}
      </Alert>
    );
  }

  return (
    <>
      <Table withTableBorder striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={260}>Field</Table.Th>
            <Table.Th>Old value</Table.Th>
            <Table.Th>New value</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {entries.map(([key, newVal]) => {
            const oldVal = cr.kind === 'UPDATE' ? live[key] : undefined;
            const isNewRecord = cr.kind === 'CREATE';
            return (
              <Table.Tr key={key}>
                <Table.Td>
                  <Code>{key}</Code>
                </Table.Td>
                <Table.Td>
                  {isNewRecord ? (
                    <Text c="dimmed" size="xs">— (new record)</Text>
                  ) : needsLiveFetch && liveQuery.isLoading ? (
                    <Skeleton height={14} width="60%" />
                  ) : needsLiveFetch && liveQuery.isError ? (
                    <Text c="dimmed" size="xs">(live row unavailable)</Text>
                  ) : (
                    formatValue(oldVal, key)
                  )}
                </Table.Td>
                <Table.Td>{formatValue(newVal, key)}</Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      {cr.kind === 'UPDATE' && (
        <Text size="xs" c="dimmed" mt="xs">
          Only fields present in this request are shown. The live row's
          other columns are unaffected by approval.
        </Text>
      )}
      {cr.kind === 'CREATE' && (
        <Text size="xs" c="dimmed" mt="xs">
          The entity does not exist yet; its id will be assigned on approval.
        </Text>
      )}
    </>
  );
}
